import { ReactNode } from 'react'
import { getFileFromNwCDN } from '~/utils/nw-cdn'

export type LeadMagnetFormField =
  | 'household_income'
  | 'age'
  | 'first_name'
  | 'last_name'
  | 'email'
  | 'schedule_call_interest'
  | 'phone'
  | 'investable_assets_range'

export const DEFAULT_FORM_FIELDS: LeadMagnetFormField[] = [
  'household_income',
  'investable_assets_range',
  'age',
  'first_name',
  'last_name',
  'email',
]

export type LeadMagnetPage = {
  /**
   * Page identifier
   *
   * It will be displayed in url
   *
   * @example
   * slug: 'rsu_mistakes'
   * url would be: https://nerdwalletwealthpartners.com/lp/rsu_mistakes
   */
  slug: string

  pageTitle: string
  headline: ReactNode
  body: ReactNode

  imageUrl?: string

  ctaText: string
  disclosure: string

  /** Fields to render in the lead capture form. Defaults to DEFAULT_FORM_FIELDS when omitted. */
  formFields?: LeadMagnetFormField[]

  /** Optional phone disclosure override. Defaults to the shared Lead Magnet TCPA disclosure. */
  phoneDisclosure?: ReactNode

  /** Makes phone required and shows the phone disclosure before any number is entered. */
  phoneRequired?: boolean

  /** Puts the submit button above the phone disclosure. Default keeps the disclosure above the button. */
  disclosurePlacement?: 'before-submit' | 'after-submit'

  /** `funnel` for light card backgrounds, where the default white `cta` pill disappears. */
  submitVariant?: 'cta' | 'funnel'

  /** Overrides the phone disclosure's default `text-body-sm` sizing. */
  disclosureClassName?: string
} & CTAType

export type LeadMagnetPageWithScheduleCTA = LeadMagnetPage & CTASchedule

type CTAType = CTARedirect | CTASchedule | CTAEmail | CTAReveal
type CTARedirect = {
  ctaType: 'redirect'
  ctaUrl: string
}
/** After submit: set unlock cookie and stay on page (caller reveals content). */
type CTAReveal = {
  ctaType: 'reveal'
}

export type LeadMagnetDownloadLink = {
  text: string
  href: string
}

type CTASchedule =
  | {
      ctaType: 'schedule'
      calendlyUrl: string
      downloadLink?: string
      downloadLinks?: never
    }
  | {
      ctaType: 'schedule'
      calendlyUrl: string
      downloadLinks: LeadMagnetDownloadLink[]
      downloadLink?: never
    }

type CTAEmail = {
  ctaType: 'email'
}

export const BOOK_A_CALL_URL =
  'https://calendly.com/d/dz43-q6s-z32/introductory-meeting-with-nerdwallet-wealth-partners/'

export const LEAD_MAGNET_PAGES: Array<LeadMagnetPage> = [
  {
    slug: 'rsu_mistakes',
    pageTitle: 'How to Avoid 5 Costly RSU Mistakes',
    headline: <>How to Avoid 5 Costly RSU Mistakes</>,
    body: 'If you receive part of your compensation in company stock, this guide is for you.',
    formFields: ['household_income', 'age', 'first_name', 'last_name', 'email', 'investable_assets_range'],
    ctaText: 'See the guide',
    ctaType: 'schedule',
    downloadLink: 'https://v.fastcdn.co/u/8c406d28/65846627-0-NWWP-5-Costly-RSU-Mi.pdf',
    calendlyUrl: BOOK_A_CALL_URL,
    imageUrl: getFileFromNwCDN('lead-magnets/rsu-dark.webp'),
    disclosure:
      'NerdWallet Wealth Partners, LLC (NWWP) is an SEC-registered investment advisor. Registration as an investment adviser does not imply a certain level of skill or training, nor does it constitute an endorsement by any securities regulator. Please see NWWP website for disclosures and important information. Nothing contained in this material is intended to constitute legal, tax, securities or investment advice, nor an opinion regarding the appropriateness of any investment. The general information contained in this publication should not be acted upon without obtaining specific legal, tax and investment advice from a licensed professional.​',
  },
  {
    slug: 'tax-traps',
    pageTitle: '10 Tax Traps High Earners Should Avoid',
    headline: '10 Tax Traps High Earners Should Avoid',
    body: 'This free tax guide outlines missteps and considerations to help you plan with confidence. Enter your email to get your copy today.',
    formFields: ['household_income', 'age', 'first_name', 'last_name', 'email', 'investable_assets_range'],
    ctaText: 'SEE THE GUIDE',
    ctaType: 'schedule',
    downloadLink: getFileFromNwCDN('lead-magnets/NWWP-10-Tax-Traps-High-Earners-Should-Avoid-05062026.pdf'),
    calendlyUrl: BOOK_A_CALL_URL,
    imageUrl: getFileFromNwCDN('lead-magnets/tax-traps-white.webp'),
    disclosure:
      'NerdWallet Wealth Partners, LLC (NWWP) is an SEC-registered investment advisor. Registration as an investment adviser does not imply a certain level of skill or training, nor does it constitute an endorsement by any securities regulator. Please see NWWP website for disclosures and important information. Nothing contained in this material is intended to constitute legal, tax, securities or investment advice, nor an opinion regarding the appropriateness of any investment. The general information contained in this publication should not be acted upon without obtaining specific legal, tax and investment advice from a licensed professional.​',
  },
  {
    slug: 'rentvsbuy',
    pageTitle: 'Should You Rent or Buy?',
    headline: 'Should You Rent or Buy?',
    body: 'Run your total cost of renting and buying, including major home buying expenses that are easy to gloss over, like ongoing maintenance, upgrades on your wish list and exit costs.',
    formFields: [
      'household_income',
      'first_name',
      'last_name',
      'email',
      'schedule_call_interest',
      'phone',
      'investable_assets_range',
    ],
    ctaText: 'SEE THE CALCULATOR',
    ctaType: 'redirect',
    ctaUrl: '/rentvsbuycalc',
    imageUrl: getFileFromNwCDN('lead-magnets/rent-vs-buy@2x.webp'),
    disclosure:
      'The calculator is provided for informational and educational purposes only. Results are hypothetical estimates based on the information you provide and should not be construed as investment, tax, or legal advice, or as a guarantee of future results. NWWP is an SEC-registered investment adviser. Registration does not imply a certain level of skill or training. See the General Disclosure.',
  },
  {
    slug: 'blueprint',
    pageTitle: 'A Money Framework Built for High Earners',
    headline: 'A Money Framework Built for High Earners',
    body: 'We created a general framework to help high-earners in their 30s, 40s and 50s know where to put their next dollar. We break down which investing strategies we typically recommend focusing on first, share watch outs and give you a checklist to help you get started.',
    formFields: [
      'household_income',
      'first_name',
      'last_name',
      'email',
      'schedule_call_interest',
      'phone',
      'investable_assets_range',
    ],
    imageUrl: getFileFromNwCDN('lead-magnets/ynd-blueprint.webp'),
    ctaText: 'Continue',
    ctaType: 'schedule',
    downloadLinks: [
      {
        text: 'Your Next Dollar Blueprint',
        href: getFileFromNwCDN('lead-magnets/NWWP-Your-Next-Dollar-Blueprint.pdf'),
      },
      {
        text: 'Your Next Dollar Blueprint Checklist',
        href: getFileFromNwCDN('lead-magnets/NWWP-Your-Next-Dollar-Blueprint-Checklist.pdf'),
      },
    ],
    calendlyUrl: BOOK_A_CALL_URL,
    disclosure:
      'NerdWallet Wealth Partners, LLC (NWWP) is an SEC-registered investment advisor. Registration as an investment adviser does not imply a certain level of skill or training, nor does it constitute an endorsement by any securities regulator. Please see NWWP website for disclosures and important information. Nothing contained in this material is intended to constitute legal, tax, securities or investment advice, nor an opinion regarding the appropriateness of any investment. The general information contained in this publication should not be acted upon without obtaining specific legal, tax and investment advice from a licensed professional.​',
  },
  {
    slug: 'ipo-lock-up',
    pageTitle: 'Create Your IPO Equity Timeline',
    headline: 'Create Your IPO Equity Timeline',
    body: "An IPO can be a game changing milestone, but it's also full of traps and complicated rules to navigate. Share your IPO date and lock-up length and we'll build a calendar complete with key moves to consider.",
    formFields: ['household_income', 'age', 'first_name', 'last_name', 'email', 'investable_assets_range'],
    ctaText: 'UNLOCK THE TOOL',
    ctaType: 'redirect',
    // Redirects to the IPO Equity Timeline tool (app/(home)/ipo_equity_timeline).
    ctaUrl: '/ipo_equity_timeline',
    imageUrl: getFileFromNwCDN('lead-magnets/ipo-lock-up-20260810.webp'),
    // Disclosure approved by Compliance via Greenboard.
    disclosure:
      'The tool is provided for informational and educational purposes only. Results are hypothetical estimates based on the information you provide and should not be construed as investment, tax, or legal advice, or as a guarantee of future results. NWWP is an SEC-registered investment adviser. Registration does not imply a certain level of skill or training. See the General Disclosure.',
  },
  {
    slug: '40',
    pageTitle: 'Behind at 40? You can still catch up.',
    headline: 'Behind at 40? You can still catch up.',
    body: 'A clear, five-phase plan to catch up on retirement in your 40s without the high-risk shortcuts.',
    formFields: ['household_income', 'age', 'first_name', 'last_name', 'email', 'investable_assets_range'],
    ctaText: 'SEE THE GUIDE',
    ctaType: 'schedule',
    downloadLink: getFileFromNwCDN('lead-magnets/NWWP-Catching-Up-at-40-Playbook-20260812.pdf'),
    calendlyUrl: BOOK_A_CALL_URL,
    imageUrl: getFileFromNwCDN('lead-magnets/catch-up-playbook-20260807.webp'),
    disclosure:
      'NerdWallet Wealth Partners, LLC (NWWP) is an SEC-registered investment advisor. Registration as an investment adviser does not imply a certain level of skill or training, nor does it constitute an endorsement by any securities regulator. Please see NWWP website for disclosures and important information. Nothing contained in this material is intended to constitute legal, tax, securities or investment advice, nor an opinion regarding the appropriateness of any investment. The general information contained in this publication should not be acted upon without obtaining specific legal, tax and investment advice from a licensed professional.​',
  },
]
